import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { merchantApi } from '../../api/config';
import { toast } from 'react-toastify';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faUsers, faCircle, faUserTie, faUserShield, faUserCog, faBuilding,
  faWifi, faClock, faDesktop, faGlobe, faRotate,
  faSkull, faExclamationTriangle, faBan, faPlug,
} from '@fortawesome/free-solid-svg-icons';
import DataTable, { Column } from '../../components/shared/DataTable';
import { swalConfirm } from '../../components/shared/swalConfirm';
import {
  UiPageHeader,
  UiStatsGrid,
  UiButton,
  StatusBadge,
} from '../../components/ui';
import type { StatCardDef, StatusDef } from '../../components/ui';

interface UserPresence {
  id: string;
  user_type: 'merchant' | 'admin' | 'admin_employee' | 'employee';
  user_id: string;
  user_name: string;
  user_email: string;
  socket_id: string;
  ip_address: string;
  user_agent: string;
  is_online: boolean;
  last_seen_at: string;
  connected_at: string;
  disconnected_at?: string;
}

interface OnlineSummary {
  total_online: number;
  merchants_online: number;
  employees_online: number;
  admins_online: number;
  admin_employees_online: number;
}

const USER_TYPE_MAP: Record<string, StatusDef> = {
  merchant:       { bg: 'bg-blue-100 dark:bg-blue-900/30',   text: 'text-blue-700 dark:text-blue-400',   icon: faBuilding,   label: 'Merchant' },
  employee:       { bg: 'bg-purple-100 dark:bg-purple-900/30', text: 'text-purple-700 dark:text-purple-400', icon: faUserTie,    label: 'Employee' },
  admin:          { bg: 'bg-red-100 dark:bg-red-900/30',     text: 'text-red-700 dark:text-red-400',     icon: faUserShield, label: 'Admin' },
  admin_employee: { bg: 'bg-orange-100 dark:bg-orange-900/30', text: 'text-orange-700 dark:text-orange-400', icon: faUserCog,    label: 'Admin Employee' },
};

const formatTimeAgo = (dateStr: string) => {
  const date = new Date(dateStr);
  const now = new Date();
  const diff = now.getTime() - date.getTime();

  const minutes = Math.floor(diff / 60000);
  const hours = Math.floor(diff / 3600000);
  const days = Math.floor(diff / 86400000);

  if (minutes < 1) return 'เมื่อสักครู่';
  if (minutes < 60) return `${minutes} นาทีที่แล้ว`;
  if (hours < 24) return `${hours} ชั่วโมงที่แล้ว`;
  return `${days} วันที่แล้ว`;
};

const parseBrowser = (userAgent: string) => {
  if (!userAgent) return 'Unknown';
  if (userAgent.includes('Chrome')) return 'Chrome';
  if (userAgent.includes('Firefox')) return 'Firefox';
  if (userAgent.includes('Safari')) return 'Safari';
  if (userAgent.includes('Edge')) return 'Edge';
  return 'Other';
};

const formatSessionDuration = (connectedAt: string, disconnectedAt: string) => {
  const duration = new Date(disconnectedAt).getTime() - new Date(connectedAt).getTime();
  const minutes = Math.floor(duration / 60000);
  const hours = Math.floor(duration / 3600000);
  if (hours > 0) return `${hours}h ${minutes % 60}m`;
  return `${minutes}m`;
};

const UserCell = ({ user, online = true }: { user: UserPresence; online?: boolean }) => (
  <div className="flex items-center gap-3">
    <div className="relative">
      <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold text-sm
        ${online
          ? 'bg-gradient-to-br from-green-400 to-emerald-600 text-white'
          : 'bg-gray-200 dark:bg-slate-600 text-gray-500 dark:text-slate-300'}`}>
        {user.user_name?.charAt(0).toUpperCase() || '?'}
      </div>
      {online && (
        <span className="absolute -bottom-0.5 -right-0.5 w-3 h-3 bg-green-500 border-2 border-white dark:border-slate-800 rounded-full"></span>
      )}
    </div>
    <div>
      <p className={`font-semibold text-sm ${online ? 'text-gray-800 dark:text-slate-100' : 'text-gray-600 dark:text-slate-300'}`}>
        {user.user_name || 'Unknown'}
      </p>
      <p className="text-xs text-gray-400 dark:text-slate-500">{user.user_email || '-'}</p>
    </div>
  </div>
);

const OnlineUsersPage: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState<OnlineSummary | null>(null);
  const [onlineUsers, setOnlineUsers] = useState<UserPresence[]>([]);
  const [recentOffline, setRecentOffline] = useState<UserPresence[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [kicking, setKicking] = useState<string | null>(null);
  const [autoRefresh, setAutoRefresh] = useState(true);

  const loadData = useCallback(async () => {
    try {
      const response = await merchantApi.get('/admin/online-users');
      if (response.data.success) {
        setSummary(response.data.data.summary);
        setOnlineUsers(response.data.data.online);
        setRecentOffline(response.data.data.recent_offline);
        setError(null);
      }
    } catch (err: any) {
      if (err.response?.status === 403) {
        setError('คุณไม่มีสิทธิ์เข้าถึงหน้านี้ (เฉพาะ Admin ngx เท่านั้น)');
      } else {
        setError('ไม่สามารถโหลดข้อมูลได้');
      }
      toast.error('ไม่สามารถโหลดข้อมูลได้');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  useEffect(() => {
    if (!autoRefresh || error) return;
    const interval = setInterval(loadData, 10000);
    return () => clearInterval(interval);
  }, [autoRefresh, loadData, error]);

  const kickUser = useCallback(async (presenceId: string, userName: string) => {
    const confirmed = await swalConfirm({
      title: 'Kick user?',
      text: `ต้องการ Kick "${userName}" ออกจากระบบหรือไม่`,
      icon: 'warning',
      confirmText: 'Kick',
      confirmColor: '#ef4444',
    });
    if (!confirmed) return;

    setKicking(presenceId);
    try {
      const response = await merchantApi.post(`/admin/online-users/${presenceId}/kick`);
      if (response.data.success) {
        toast.success(`Kicked ${userName} สำเร็จ!`);
        loadData();
      }
    } catch {
      toast.error('ไม่สามารถ Kick user ได้');
    } finally {
      setKicking(null);
    }
  }, [loadData]);

  const statCards: StatCardDef[] = useMemo(() => [
    {
      icon: faWifi,
      iconClassName: 'bg-green-50 dark:bg-green-900/30 text-green-600 dark:text-green-400',
      label: 'Online ทั้งหมด',
      value: <span className="text-green-600 dark:text-green-400">{summary?.total_online ?? 0}</span>,
      subValue: <span className="text-xs text-gray-400 dark:text-slate-500">เชื่อมต่ออยู่ตอนนี้</span>,
    },
    {
      icon: faBuilding,
      iconClassName: 'bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400',
      label: 'Merchants',
      value: <span className="text-blue-600 dark:text-blue-400">{summary?.merchants_online ?? 0}</span>,
      subValue: <span className="text-xs text-gray-400 dark:text-slate-500">ออนไลน์</span>,
    },
    {
      icon: faUserTie,
      iconClassName: 'bg-purple-50 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400',
      label: 'Employees',
      value: <span className="text-purple-600 dark:text-purple-400">{summary?.employees_online ?? 0}</span>,
      subValue: <span className="text-xs text-gray-400 dark:text-slate-500">ออนไลน์</span>,
    },
    {
      icon: faUserShield,
      iconClassName: 'bg-red-50 dark:bg-red-900/30 text-red-600 dark:text-red-400',
      label: 'Admins',
      value: <span className="text-red-600 dark:text-red-400">{summary?.admins_online ?? 0}</span>,
      subValue: <span className="text-xs text-gray-400 dark:text-slate-500">ออนไลน์</span>,
    },
    {
      icon: faUserCog,
      iconClassName: 'bg-orange-50 dark:bg-orange-900/30 text-orange-600 dark:text-orange-400',
      label: 'Admin Employees',
      value: <span className="text-orange-600 dark:text-orange-400">{summary?.admin_employees_online ?? 0}</span>,
      subValue: <span className="text-xs text-gray-400 dark:text-slate-500">ออนไลน์</span>,
    },
  ], [summary]);

  const onlineColumns: Column<UserPresence>[] = useMemo(() => [
    {
      key: 'user',
      header: 'User',
      render: (user) => <UserCell user={user} online />,
    },
    {
      key: 'user_type',
      header: 'Type',
      render: (user) => <StatusBadge status={user.user_type} statusMap={USER_TYPE_MAP} />,
    },
    {
      key: 'ip_address',
      header: 'IP Address',
      render: (user) => (
        <span className="flex items-center gap-1.5 text-sm text-gray-600 dark:text-slate-300">
          <FontAwesomeIcon icon={faGlobe} className="text-gray-400 dark:text-slate-500" />
          {user.ip_address || '-'}
        </span>
      ),
    },
    {
      key: 'browser',
      header: 'Browser',
      render: (user) => (
        <span className="flex items-center gap-1.5 text-sm text-gray-600 dark:text-slate-300">
          <FontAwesomeIcon icon={faDesktop} className="text-gray-400 dark:text-slate-500" />
          {parseBrowser(user.user_agent)}
        </span>
      ),
    },
    {
      key: 'connected_at',
      header: 'Connected',
      render: (user) => (
        <span className="flex items-center gap-1.5 text-xs text-gray-500 dark:text-slate-400">
          <FontAwesomeIcon icon={faClock} className="text-gray-400 dark:text-slate-500" />
          {formatTimeAgo(user.connected_at)}
        </span>
      ),
    },
    {
      key: 'last_seen_at',
      header: 'Last Seen',
      render: (user) => (
        <span className="text-xs text-gray-500 dark:text-slate-400">{formatTimeAgo(user.last_seen_at)}</span>
      ),
    },
    {
      key: 'actions',
      header: 'Actions',
      render: (user) => (
        <UiButton
          variant="danger"
          icon={faSkull}
          onClick={() => kickUser(user.id, user.user_name)}
          disabled={kicking === user.id}
          loading={kicking === user.id}
          className="!px-3 !py-1.5 !text-xs mx-auto"
        >
          Kick
        </UiButton>
      ),
    },
  ], [kicking, kickUser]);

  const offlineColumns: Column<UserPresence>[] = useMemo(() => [
    {
      key: 'user',
      header: 'User',
      render: (user) => <UserCell user={user} online={false} />,
    },
    {
      key: 'user_type',
      header: 'Type',
      render: (user) => <StatusBadge status={user.user_type} statusMap={USER_TYPE_MAP} />,
    },
    {
      key: 'ip_address',
      header: 'IP Address',
      render: (user) => (
        <span className="text-sm text-gray-500 dark:text-slate-400">{user.ip_address || '-'}</span>
      ),
    },
    {
      key: 'disconnected_at',
      header: 'Disconnected',
      render: (user) => (
        <span className="text-xs text-gray-500 dark:text-slate-400">
          {user.disconnected_at ? formatTimeAgo(user.disconnected_at) : '-'}
        </span>
      ),
    },
    {
      key: 'duration',
      header: 'Session Duration',
      render: (user) => (
        <span className="text-xs text-gray-400 dark:text-slate-500">
          {user.connected_at && user.disconnected_at
            ? formatSessionDuration(user.connected_at, user.disconnected_at)
            : '-'}
        </span>
      ),
    },
  ], []);

  if (error) {
    return (
      <div className="max-w-full">
        <UiPageHeader
          icon={faWifi}
          title="Online Users Monitor"
          subtitle="Real-time monitoring ผ่าน Socket.IO"
        />
        <div className="max-w-2xl mx-auto mt-8">
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800/50 rounded-2xl p-8 text-center">
            <FontAwesomeIcon icon={faBan} className="text-6xl text-red-400 dark:text-red-500 mb-4" />
            <h2 className="text-2xl font-bold text-red-700 dark:text-red-400 mb-2">Access Denied</h2>
            <p className="text-red-600 dark:text-red-300">{error}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-full">
      <UiPageHeader
        icon={faWifi}
        title="Online Users Monitor"
        subtitle="เฉพาะ Admin ngx • Real-time monitoring"
        below={
          <span className="inline-flex items-center gap-1.5 mt-2 text-sm font-medium bg-green-500/20 text-green-400 px-3 py-0.5 rounded-full">
            <FontAwesomeIcon icon={faCircle} className="text-[8px] animate-pulse" />
            {summary?.total_online ?? 0} Online
          </span>
        }
        actions={
          <div className="flex flex-wrap items-center gap-3">
            <label className="flex items-center gap-2 text-white/80 text-sm cursor-pointer">
              <input
                type="checkbox"
                checked={autoRefresh}
                onChange={(e) => setAutoRefresh(e.target.checked)}
                className="w-4 h-4 rounded"
              />
              Auto Refresh (10s)
            </label>
            <button
              type="button"
              onClick={() => loadData()}
              disabled={loading}
              className="px-4 py-2 rounded-lg text-sm font-medium bg-white/20 hover:bg-white/30 disabled:opacity-50 text-white flex items-center gap-2 backdrop-blur-sm border border-white/20"
            >
              <FontAwesomeIcon icon={faRotate} className={loading ? 'animate-spin' : ''} />
              Refresh
            </button>
          </div>
        }
      />

      <UiStatsGrid cards={statCards} loading={loading} cols={5} className="mb-6" />

      <DataTable
        data={onlineUsers}
        columns={onlineColumns}
        loading={loading}
        keyExtractor={(user) => user.id}
        emptyMessage="ไม่มี user ออนไลน์"
        emptyIcon={<FontAwesomeIcon icon={faPlug} className="text-5xl text-gray-200 dark:text-slate-600" />}
        headerContent={
          <h2 className="text-lg font-bold text-gray-800 dark:text-slate-100 flex items-center gap-2">
            <FontAwesomeIcon icon={faWifi} className="text-green-500" />
            Online Users ({onlineUsers.length})
          </h2>
        }
        hoverable
        className="mb-6"
      />

      <DataTable
        data={recentOffline}
        columns={offlineColumns}
        loading={loading}
        keyExtractor={(user) => user.id}
        emptyMessage="ไม่มีข้อมูล"
        emptyIcon={<FontAwesomeIcon icon={faUsers} className="text-5xl text-gray-200 dark:text-slate-600" />}
        pagination={{ pageSize: 20, showPageSizeSelector: false }}
        headerContent={
          <h2 className="text-lg font-bold text-gray-800 dark:text-slate-100 flex items-center gap-2">
            <FontAwesomeIcon icon={faPlug} className="text-gray-400 dark:text-slate-500" />
            Recent Offline (24h) — {recentOffline.length}
          </h2>
        }
        hoverable
        striped
        className="mb-6"
      />

      <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800/50 rounded-xl p-4 flex items-start gap-3">
        <FontAwesomeIcon icon={faExclamationTriangle} className="text-amber-500 dark:text-amber-400 mt-0.5 flex-shrink-0" />
        <div className="text-sm text-amber-800 dark:text-amber-300">
          <strong>หมายเหตุ:</strong> หน้านี้แสดงผู้ใช้ที่เชื่อมต่อผ่าน Socket.IO เท่านั้น
          ผู้ใช้ที่เปิดหน้าเว็บแต่ไม่ได้ใช้งาน Socket อาจไม่แสดงในรายการนี้
        </div>
      </div>
    </div>
  );
};

export default OnlineUsersPage;
